import { useQuery } from '@tanstack/react-query';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortModel,
} from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import {
    ACTIVE_WIDTH,
    NAME_MIN_WIDTH,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import {
    BusinessDefaultProduct,
    Product,
    ProductSubtype,
    ProductType,
} from '@barboxx/models';
import { getProductName } from '@barboxx/utils';

type Columns = GridColDef<BusinessDefaultProduct.DetailsResponse>[];
const COLUMNS: Columns = [
    {
        field: 'isActive',
        headerName: 'Active',
        width: ACTIVE_WIDTH,
        editable: true,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
];

interface Props {
    data: BusinessDefaultProduct[];
    size: number;
    page: number;
    total: number;
    onRowClick: (id: string) => void;
    onChangeSort: (sort: GridSortModel) => void;
    handleChangePageModel: (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => void;
}

export function BusinessDefaultProductTable(props: Props) {
    const {
        data = [],
        total,
        size,
        page,
        onRowClick,
        handleChangePageModel,
        onChangeSort,
    } = props;

    const { businessList } = useBusinessSelector();

    const productsQuery = useQuery({
        queryKey: ['products'],
        queryFn: () => Api.Products.Products.getProducts({ size: 1000 }),
    });
    const productTypesQuery = useQuery({
        queryKey: ['productTypes'],
        queryFn: () => Api.Products.Types.getProductTypes({ size: 50 }),
    });
    const productSubtypeQuery = useQuery({
        queryKey: ['productSubtypes'],
        queryFn: () => Api.Products.Subtypes.getProductSubtypes({ size: 100 }),
    });

    const handleRowClick = (param: GridRowParams) => {
        onRowClick(param.id as string);
    };

    const columns: Columns = [
        {
            field: 'businessId',
            headerName: 'Business',
            width: NAME_MIN_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    businessList.map(business => ({
                        value: business.id,
                        label: business.place.name,
                    })) ?? []
                );
            },
        },
        {
            field: 'productId',
            headerName: 'Product',
            editable: true,
            width: 200,
            flex: 1,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    productsQuery.data?.data?.results.map(
                        (product: Product) => ({
                            value: product.id,
                            label: getProductName(product),
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'productTypeId',
            headerName: 'Alcohol Type',
            editable: true,
            width: 200,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    productTypesQuery.data?.data?.results.map(
                        (productType: ProductType) => ({
                            value: productType.id,
                            label: productType.name,
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'productSubtypeId',
            headerName: 'Alcohol Subtype',
            editable: true,
            width: 200,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    productSubtypeQuery.data?.data?.results.map(
                        (productSubtype: ProductSubtype) => ({
                            value: productSubtype.id,
                            label: productSubtype.name,
                        })
                    ) ?? []
                );
            },
        },
        ...COLUMNS,
    ];
    return (
        <DataGridCard title="Default Products">
            <DataGrid
                rows={data}
                columns={columns}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                rowCount={total}
                disableColumnMenu
                onRowClick={handleRowClick}
                onSortModelChange={onChangeSort}
            />
        </DataGridCard>
    );
}
